import { createMemo, Match, Switch } from 'solid-js';
import { resizeObjectCmd } from 'Shared/posters/project/commands';
import { setObjectPositionSize } from 'Shared/posters/project/objects';
import { DrawResizeHandles } from 'Shared/posters/canvas/DrawResizeHandles';
import { applyResizeDelta, applyAspectResizeDelta, isCornerHandle, type ResizeBounds } from 'Shared/posters/canvas/utils';
import type { ObjectContext } from 'Shared/posters/project/objects';
import type { SelectionContext } from 'Shared/posters/canvas/selection';
import type { DragContext } from 'Shared/posters/canvas/drag';
import type { HistoryContext } from 'Shared/posters/project/history';
import { ResizeMode } from 'Shared/posters/canvas/interactions/ResizeMode';
import { type DraggableImpl } from 'Shared/posters/canvas/interactions/ObjectImpl';
import { useUser } from 'Shared/SiteProvider';
import type { Canvas } from 'Shared/posters/project/posterSchema';
import { TextResizeHandles } from 'Shared/posters/canvas/interactions/TextResizeHandles';
import { cloneBox, type Box, type Position } from 'Shared/posters/types';

interface IResizeHandles {
	objects:      ObjectContext;
	selection:    SelectionContext;
	history:      HistoryContext;
	drag:         DragContext;
	impl:         DraggableImpl;
	canvas:       Canvas;
	resizeMode:   ResizeMode;
	parentOffset: (() => Position) | undefined;
}


export function ResizeHandles(P: IResizeHandles)
{
	// effectiveBox/getBBox take the same `!useUser()` argument SelectableObject passes when it draws
	// the selection rect, so the handles sit on the identical (possibly clamped) box.
	const box = createMemo(() => P.impl.effectiveBox(!useUser()));
//XXX can parentOffset change? Perhaps we don't need the creatememo() here?
	const parentOffset = createMemo(() => P.parentOffset?.() ?? {x:0,y:0});

	const absLeft = () => parentOffset().x + box().pos.x;
	const absTop  = () => parentOffset().y + box().pos.y;

	return (
		<g transform={`translate(${absLeft()}, ${absTop() + P.impl.contentOffsetY()})`}>
			<Switch>
				<Match when={P.impl.type()=='text'}>
					<TextResizeHandles {...P} />
				</Match>
				<Match when={true}>
					<DrawResizeHandles impl={P.impl} 
						onHandleDown={(handle,e) =>
							resize(e,P.impl,handle,P.canvas,P.drag,P.objects,P.history,P.impl.box,box())
						}
					/>
				</Match>
			</Switch>
		</g>
	);
}

function resize(e:MouseEvent,impl:DraggableImpl,handle:string,canvas:Canvas,dragCtx:DragContext,objCtx:ObjectContext,histCtx:HistoryContext,storeBox:Box,effectiveBox:Box)
{
	e.preventDefault();
	e.stopPropagation();
	// Clamp against the effective (rendered) position. For a flex-laid-out child the
	// container owns the position, so pos.left/top is a stale design value; using it
	// would cap the size short of the container edge by that stale offset. effLeft/effTop
	// equal pos.left/top for absolute elements, so this is a no-op there.
	// Seed from the *drawn* box: for an auto-grown container effectiveBox.top is anchored per layout
	// and effectiveBox.height is the grown height, while pos.top/height still hold the smaller designed
	// values. Pairing the anchored top with the stale height snapped the (anchor) edge and jumped the
	// box. effectiveBox.height === pos.height for everything that doesn't auto-grow (images), so this is
	// behaviour-preserving there. Width isn't grown (effectiveBox.width is a dummy 0), so keep pos.width.
	const start: Box = { pos: effectiveBox.pos, dims: { width: storeBox.dims.width, height: effectiveBox.dims.height } };
	const hugFit  = impl.getHugFit();
	const bounds: ResizeBounds = { max: { width: canvas.width ?? Infinity, height: canvas.height ?? Infinity }, min: hugFit?.box.dims };
	const aspect  = isCornerHandle(handle) && impl.lockAspect();
	dragCtx.beginResize({ x: e.clientX, y: e.clientY },
		(dx, dy) => { impl.writeGeometry(aspect ? applyAspectResizeDelta(handle, start, dx, dy, bounds)
		                                        : applyResizeDelta(handle, start, dx, dy, bounds)) },
		() => {
			const after: Box = cloneBox(storeBox);
			setObjectPositionSize(objCtx, impl.id, after.pos, after.dims);
			histCtx.execute(resizeObjectCmd(objCtx, impl.id, start, after));
		},
	)
}

